import { isAxiosError } from "axios"
import { HttpClient } from "@/shared/lib/http-client"
import { env } from "@/config/env"
import type { UserProfile, UserProfileInput } from "../types"

/** Backend response envelope: `{ data }` on success, `{ error }` on failure. */
interface DataEnvelope<T> {
  data: T
}

export interface IUserService {
  readonly isConfigured: boolean
  upsertMe(token: string, profile: UserProfileInput): Promise<UserProfile>
  getMe(token: string): Promise<UserProfile | null>
}

/**
 * Client for the application backend user profile (`/v1/users/me`).
 * Every call is authenticated with the caller's Firebase ID token.
 */
export class UserService extends HttpClient implements IUserService {
  readonly isConfigured: boolean

  constructor(baseURL: string) {
    super({ baseURL })
    this.isConfigured = Boolean(baseURL)
  }

  /** Create or update the current user's profile. */
  async upsertMe(token: string, profile: UserProfileInput): Promise<UserProfile> {
    const body = await this.post<DataEnvelope<UserProfile>>("/v1/users/me", profile, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return body.data
  }

  /** Fetch the current user's profile; null when it doesn't exist yet (404). */
  async getMe(token: string): Promise<UserProfile | null> {
    try {
      const body = await this.get<DataEnvelope<UserProfile>>("/v1/users/me", {
        headers: { Authorization: `Bearer ${token}` },
      })
      return body.data
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) return null
      throw err
    }
  }
}

export const userService: IUserService = new UserService(env.VITE_API_URL)
